import React, { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { Maximize2 } from 'lucide-react';

import { getLayoutDetail, whDomain, type LayoutDetailRack } from '@/api/analytics';
import { ShelfTimeseriesChart } from '@/pages/Management/AnalyticsLayoutDetail';
import { useFrameTime } from '@/components/ui/hover-time';
import { cn } from '@/lib/utils';
import { DayChartLightbox } from './DayChartLightbox';
import { useChartTouchTooltipGuard } from './chartTouchTooltipGuard';
import { buildRackStrip, type DayStripPhoto } from './DayPhotoStrip';

// "Все стеллажи" mode for the Выкладка charts: a grid of compact per-rack
// shelf charts (no photos — the point is comparing the racks' dynamics at a
// glance). Clicking a card switches back to the normal single-rack view for
// that rack, as if it were picked in the selector. Also reused by the week
// report's per-rack/per-day chart grid.

/** One compact per-rack shelf chart; fetches its own layout-detail slice. */
export function RackMiniChart({
  zoneId,
  from,
  to,
  rackId,
  title,
  height = 150,
  onOpen,
  mini = false,
  onData,
}: {
  zoneId: string;
  from: string;
  to: string;
  rackId: number;
  title: string;
  height?: number;
  onOpen?: () => void;
  /** Axis-less grid-cell mode (uniform 0–100% scale, compact tooltip). */
  mini?: boolean;
  /** Reports whether this cell actually has data — the week grid hides
   *  all-empty rows/columns instead of painting «нет данных» cells. */
  onData?: (hasData: boolean) => void;
}) {
  const { data } = useSWR(
    zoneId ? ['report:rack-mini', zoneId, rackId, from, to] : null,
    () =>
      getLayoutDetail(zoneId, from, to, rackId, '15min', {
        // The mini card draws no photo strip; ask for the minimum the API allows.
        photoMode: 'even',
        photoCount: 1,
      }).then((r) => r.data),
    { keepPreviousData: true, revalidateOnFocus: false },
  );

  const hasData = data
    ? data.shelf_timeseries.points.length > 0 && data.shelf_timeseries.shelves.length > 0
    : null;
  const onDataRef = React.useRef(onData);
  onDataRef.current = onData;
  React.useEffect(() => {
    if (hasData != null) onDataRef.current?.(hasData);
  }, [hasData]);

  const domain = useMemo<[number, number] | undefined>(() => {
    const wh = data?.working_hours;
    if (wh) {
      const d = whDomain(wh); // padded VISUAL domain (888)
      const s = new Date(d.start).getTime();
      const e = new Date(d.end).getTime();
      if (Number.isFinite(s) && Number.isFinite(e) && e > s) return [s, e];
    }
    return undefined;
  }, [data?.working_hours]);

  // Scrolling a GRID of these must not open/strand touch tooltips.
  const touchTooltipGuard = useChartTouchTooltipGuard();

  return (
    <div
      {...touchTooltipGuard}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      className={cn(
        'group relative w-full rounded-xl border border-border bg-card p-2 text-left',
        onOpen && 'cursor-pointer transition hover:border-primary/40 hover:shadow-sm',
      )}
    >
      {/* Click affordance — always faintly there, clear on hover. */}
      {onOpen && (
        <Maximize2 className="absolute right-1.5 top-1.5 z-10 size-3.5 text-muted-foreground/40 transition-colors group-hover:text-primary" />
      )}
      {title && <div className="mb-1 truncate px-1 text-xs font-medium text-foreground">{title}</div>}
      {data ? (
        <ShelfTimeseriesChart
          data={data.shelf_timeseries}
          granularity="15min"
          label=""
          bare
          height={height}
          domain={domain}
          showLegend={false}
          mini={mini}
        />
      ) : (
        <div className="animate-pulse rounded-lg bg-muted" style={{ height }} />
      )}
    </div>
  );
}

// Rack crops are tall + narrow (same constant as DayRacksCard).
const RACK_SLOT_W = 72;

/** Fullscreen view for one rack × one day — the same photos-first lightbox the
 *  day report uses, launched from a week-grid cell. Fetches its own slice. */
export function RackDayLightbox({
  zoneId,
  day,
  rackId,
  rackName,
  onClose,
}: {
  zoneId: string;
  day: string;
  rackId: number;
  rackName: string;
  onClose: () => void;
}) {
  const ft = useFrameTime();
  const { data } = useSWR(
    ['report:rack-day-lb', zoneId, rackId, day],
    () =>
      getLayoutDetail(zoneId, day, day, rackId, '15min', {
        photoMode: 'even',
        photoCount: 12,
      }).then((r) => r.data),
    { revalidateOnFocus: false },
  );

  const domain = useMemo<[number, number] | null>(() => {
    const wh = data?.working_hours;
    if (wh) {
      const d = whDomain(wh); // padded VISUAL domain (888)
      const s = new Date(d.start).getTime();
      const e = new Date(d.end).getTime();
      if (Number.isFinite(s) && Number.isFinite(e) && e > s) return [s, e];
    }
    return null;
  }, [data?.working_hours]);

  const loadWindowPhotos = useCallback(
    async (dom: [number, number], count: number): Promise<DayStripPhoto[]> => {
      const r = await getLayoutDetail(zoneId, day, day, rackId, '15min', {
        photoMode: 'even',
        photoCount: count,
        fromTs: new Date(dom[0]).toISOString(),
        toTs: new Date(dom[1]).toISOString(),
      });
      return buildRackStrip(r.data.rack_photos ?? []);
    },
    [zoneId, day, rackId],
  );

  if (!data || !domain) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
        <div className="h-40 w-full max-w-lg animate-pulse rounded-xl bg-card" />
      </div>
    );
  }

  const dayLabel = new Date(`${day}T00:00:00`).toLocaleDateString('ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  });

  return (
    <DayChartLightbox
      title={`${rackName} · ${dayLabel}`}
      photos={buildRackStrip(data.rack_photos ?? [])}
      captionOf={(p) => {
        const tlabel = ft(p.at);
        return `${p.metric ? `${p.metric} · ` : ''}${tlabel}`.trim();
      }}
      fullDomain={domain}
      slotW={RACK_SLOT_W}
      plotInset={{ left: 44, right: 8, bottom: 52 }}
      renderChart={({ domain: dom, height }) => (
        <ShelfTimeseriesChart
          data={data.shelf_timeseries}
          granularity="15min"
          label=""
          bare
          height={height}
          domain={dom}
        />
      )}
      loadWindowPhotos={loadWindowPhotos}
      // Fullscreen = MORE photos (2 staggered rows), same rule as Столы.
      stripLanes={2}
      // Rack strip: cover the whole day, don't let opening-ramp dips crowd the
      // afternoon out (Alexey zone 19 2026-07-22).
      coverageFirst
      onClose={onClose}
    />
  );
}

/** The grid of per-rack mini charts for one day. */
export function RackMiniGrid({
  zoneId,
  day,
  racks,
  onPick,
  chartHeight = 150,
  className,
}: {
  zoneId: string;
  day: string;
  racks: LayoutDetailRack[];
  onPick: (rackId: number) => void;
  chartHeight?: number;
  className?: string;
}) {
  return (
    <div className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', className)}>
      {racks.map((r) => (
        <RackMiniChart
          key={r.id}
          zoneId={zoneId}
          from={day}
          to={day}
          rackId={r.id}
          title={r.name}
          height={chartHeight}
          onOpen={() => onPick(r.id)}
        />
      ))}
    </div>
  );
}
